import { Dialog, IconButton, Box, AppBar, Toolbar, Typography, ImageList, ImageListItem, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { useHomeAttachmentsContext } from '../contexts/HomeAttachmentsContext';
import ImageViewer from './ImageViewer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

interface AllImagesModalProps {
  open: boolean;
  onClose: () => void;
}

const AllImagesModal = ({ open, onClose }: AllImagesModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  const {
    attachments,
    isLoading,
    error,
    getAttachmentUrl,
    isImageFile,
    deleteAttachment
  } = useHomeAttachmentsContext();
  
  // Filter for only image attachments
  const imageAttachments = attachments.filter(att => isImageFile(att.fileType));

  // Get image URLs for display in the grid
  const imageUrls = imageAttachments.map(att => getAttachmentUrl(att));

  // Calculate columns based on screen size
  const getCols = () => {
    if (isMobile) return 2;
    if (isTablet) return 3;
    return 4;
  };
  
  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setViewerOpen(true);
  };
  
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      sx={{ zIndex: 2100 }}
    >
      <Box sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <AppBar position="static" color="primary" elevation={1}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              All Images
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : imageUrls.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography>No images available</Typography>
            </Box>
          ) : (
            <ImageList cols={getCols()} gap={16}>
              {imageUrls.map((url, index) => (
                <ImageListItem key={index} onClick={() => handleImageClick(index)}>
                  <img
                    src={url}
                    alt={`Home image ${index + 1}`}
                    loading="lazy"
                    style={{
                      width: '100%',
                      aspectRatio: '4/3',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </Box>
      </Box>
      
      {/* Image Viewer for full-size display */}
      <ImageViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        attachments={imageAttachments}
        initialIndex={selectedImageIndex}
        onDelete={async (attachment) => {
          const remaining = imageAttachments.length - 1;
          const success = await deleteAttachment(attachment.key);
          return { success, remainingCount: remaining };
        }}
      />
    </Dialog>
  );
};

export default AllImagesModal;